// Formatting, unit-id and diff helpers shared by the talk-track pages.

import type {
  RateRange,
  StatsFilters,
  TalkTrackContent,
  TopicDetail,
  UnitStats,
  VersionStatus,
} from "../../types/talkTrack";
import { sameValue } from "../history/VersionHistory";

// Units below this many moves get a low-sample badge and are never flagged
// as weak — same guard as the evals version comparison.
export const LOW_SAMPLE = 20;

// Topic and version×arm rows from fewer users than this are flagged (the
// server sends its own `lowSampleUsers`; this is the fallback).
export const LOW_SAMPLE_USERS = 30;

export const NO_FILTERS: StatsFilters = { inputMode: null, excludeTopUser: false };

export const filtersActive = (f: StatsFilters): boolean => f.inputMode !== null || f.excludeTopUser;

// "41–68%"
export function rangeText(range: RateRange | null | undefined): string | null {
  if (!range) return null;
  return `${Math.round(range.low * 100)}–${Math.round(range.high * 100)}%`;
}

// Two rates differ beyond what sampling explains only when both come from
// at least `minUsers` users and their ranges don't overlap; without both
// ranges there is no claim to make. Users are counted per rate: a row of 40
// users may have an offer acceptance from 2 of them.
export function rangesApart(
  a: RateRange | null | undefined,
  b: RateRange | null | undefined,
  minUsers = 0,
): boolean {
  if (!a || !b || a.users < minUsers || b.users < minUsers) return false;
  return a.high < b.low || b.high < a.low;
}

export type ContentTab = "explore" | "patterns" | "explainers" | "modules" | "screens";

export function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

export function statusLabel(status: VersionStatus): string {
  return status.replace("_", " ");
}

// Weak = enough moves to trust the numbers, and either under-answered or a
// conversation-ender.
export function isWeakUnit(u: UnitStats | undefined): boolean {
  if (!u || u.moves < LOW_SAMPLE) return false;
  return (u.answerRate !== null && u.answerRate < 0.6) || (u.dropOffRate !== null && u.dropOffRate > 0.15);
}

// "412× · 86% answered · 4% drop-off" (+ offer acceptance for offer units).
export function unitStatsLine(u: UnitStats): string {
  const parts = [`${u.moves}×`];
  if (u.answerRate !== null) parts.push(`${pct(u.answerRate)} answered`);
  if (u.dropOffRate !== null) parts.push(`${pct(u.dropOffRate)} drop-off`);
  if (u.offerAcceptance !== null) parts.push(`${pct(u.offerAcceptance)} accepted`);
  return parts.join(" · ");
}

// Newline-separated list fields.
export function parseList(text: string): string[] {
  return text.split("\n").map((s) => s.trim()).filter(Boolean);
}

export function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

export function unitDomId(unitId: string): string {
  return `unit-${unitId}`;
}

export function allUnitIds(content: TalkTrackContent): Set<string> {
  const ids = new Set<string>();
  for (const t of content.tracks) {
    ids.add(t.id);
    t.questions.forEach((q) => ids.add(q.id));
    (t.probes ?? []).forEach((p) => p.id && ids.add(p.id));
  }
  content.explainers.forEach((e) => ids.add(e.id));
  content.modules.forEach((m) => ids.add(m.id));
  content.safetyScreens.forEach((s) => ids.add(s.id));
  (content.patterns ?? []).forEach((p) => ids.add(p.id));
  return ids;
}

// Unit ids must stay stable (per-unit stats key on them), so new units get a
// generated `<prefix><n>` id that isn't taken yet, e.g. anxiety.frequency.q9.
export function nextUnitId(content: TalkTrackContent, prefix: string, first = 1): string {
  const taken = allUnitIds(content);
  for (let n = first; ; n++) {
    const id = `${prefix}${n}`;
    if (!taken.has(id)) return id;
  }
}

// The content tab a unit lives on (for scroll-to-unit from a check/comment).
export function unitTab(content: TalkTrackContent, unitId: string): ContentTab | null {
  if (
    content.tracks.some(
      (t) => t.id === unitId || t.questions.some((q) => q.id === unitId) || (t.probes ?? []).some((p) => p.id === unitId),
    )
  ) {
    return "explore";
  }
  if ((content.patterns ?? []).some((p) => p.id === unitId)) return "patterns";
  if (content.explainers.some((e) => e.id === unitId)) return "explainers";
  if (content.modules.some((m) => m.id === unitId)) return "modules";
  if (content.safetyScreens.some((s) => s.id === unitId)) return "screens";
  return null;
}

// Where a version selector should land by default: work in progress first,
// then what's being served, then the newest version.
export function defaultVersion(detail: TopicDetail): number | null {
  const byStatus = (s: VersionStatus) => detail.versions.find((v) => v.status === s)?.version;
  return (
    byStatus("draft") ??
    byStatus("in_review") ??
    byStatus("approved") ??
    detail.topic.candidateVersion ??
    detail.topic.liveVersion ??
    detail.versions[0]?.version ??
    null
  );
}

// ---- unit-level diff (History tab)

export interface DiffUnit {
  id: string;
  kind: string;
  label: string;
}

export interface ContentDiff {
  added: DiffUnit[];
  removed: DiffUnit[];
  changed: DiffUnit[];
  summaryChanged: boolean;
  entrySignalsChanged: boolean;
  bridgeChanged: boolean;
  closingsChanged: boolean;
}

function flatten(content: TalkTrackContent): Map<string, { unit: DiffUnit; value: unknown }> {
  const out = new Map<string, { unit: DiffUnit; value: unknown }>();
  const put = (id: string, kind: string, label: string, value: unknown) =>
    out.set(id, { unit: { id, kind, label }, value });
  for (const t of content.tracks) {
    // Questions diff as their own units, so a track only "changes" when its
    // own fields do.
    const { questions, ...own } = t;
    put(t.id, "track", t.name, own);
    questions.forEach((q) => put(q.id, "question", q.text, q));
  }
  content.explainers.forEach((e) => put(e.id, "explainer", e.title, e));
  content.modules.forEach((m) => put(m.id, "module", m.title, m));
  content.safetyScreens.forEach((s) => put(s.id, "safety screen", s.question, s));
  (content.patterns ?? []).forEach((p) => put(p.id, "pattern", p.name, p));
  return out;
}

export function diffContent(base: TalkTrackContent, next: TalkTrackContent): ContentDiff {
  const a = flatten(base);
  const b = flatten(next);
  const added: DiffUnit[] = [];
  const removed: DiffUnit[] = [];
  const changed: DiffUnit[] = [];
  for (const [id, entry] of b) {
    const prev = a.get(id);
    if (!prev) added.push(entry.unit);
    else if (!sameValue(prev.value, entry.value)) changed.push(entry.unit);
  }
  for (const [id, entry] of a) if (!b.has(id)) removed.push(entry.unit);
  return {
    added,
    removed,
    changed,
    summaryChanged: base.summary !== next.summary,
    entrySignalsChanged: !sameValue(base.entrySignals, next.entrySignals),
    bridgeChanged: (base.bridge ?? "") !== (next.bridge ?? ""),
    closingsChanged: !sameValue(base.closings ?? [], next.closings ?? []),
  };
}

// Above this share of moves from one user, a row's numbers mostly describe
// that one person.
export const SKEW_SHARE = 0.5;

export const skewNote = (share: number | null | undefined): string | null =>
  share != null && share >= SKEW_SHARE ? `1 user = ${Math.round(share * 100)}% of moves` : null;
